import React, { useState, useContext, useEffect, useMemo } from "react";
import { Box, Typography, Stack, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import axios from "../../api/axios";

import GeneralContext from "../GeneralContext/GeneralContext";
import PageBody from "../GeneralComponents/PageBody";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageBgBlock from "../GeneralComponents/PageBgBlock";

// =====================
// Días hábiles (lunes-viernes)
// =====================
function isBusinessDay(date) {
  const d = date.getDay();
  return d !== 0 && d !== 6;
}

function nthBusinessDay(year, month0, n) {
  let count = 0;
  for (let day = 1; day <= 31; day++) {
    const dt = new Date(year, month0, day);
    if (dt.getMonth() !== month0) break;
    if (isBusinessDay(dt)) {
      count += 1;
      if (count === n) return dt;
    }
  }
  return new Date(year, month0 + 1, 0);
}

const MESES_ES = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE",
];

// Mapeo de metadata_tipo → vw_fuente (mismo criterio que estado_recepcion)
const TIPO_TO_VW = {
  PREFACTURA:              "vw_prefactura",
  ALTASBAJAS:              "vw_altas_bajas",
  MALICIOSAS:              "vw_maliciosas",
  CAMBIOCONFIGURACION:     "vw_cambio_configuracion",
  MESAAYUDA:               "vw_mesa_ayuda",
  TICKETSMESAAYUDA:        "vw_tickets_mesa_ayuda",
  MANTENIMIENTO:           "vw_mantenimiento",
  VISITASZONA:             "vw_visitas_zona",
  VISITASTOP10:            "vw_visitas_top10",
  VISITASINTERESMINEDUC:   "vw_visitas_interes_mineduc",
  CONTROLPARENTAL:         "vw_control_parental",
  SEGURIDAD:               "vw_seguridad",
  VISITASPORCATEGORIA:     "vw_visitas_por_categoria",
  DOMINIOSVISITADOS:       "vw_dominios_visitados",
  DOMINIOSINTERESVISITADOS:"vw_dominios_interes_visitados",
  ACUERDOSPRIVADOS:        "vw_acuerdosprivados",
};

const VW_LABELS = {
  vw_prefactura:               "Prefactura",
  vw_altas_bajas:              "Altas/Bajas",
  vw_maliciosas:               "Páginas maliciosas",
  vw_cambio_configuracion:     "Cambios de configuración",
  vw_mesa_ayuda:               "Mesa de ayuda",
  vw_tickets_mesa_ayuda:       "Tickets mesa de ayuda",
  vw_mantenimiento:            "Labores de mantenimiento",
  vw_visitas_zona:             "Visitas mensuales por zona",
  vw_visitas_top10:            "Top 10 dominios por zona",
  vw_visitas_interes_mineduc:  "Dominios interés ministerial",
  vw_control_parental:         "Control parental",
  vw_seguridad:                "Eventos de seguridad",
  vw_visitas_por_categoria:    "Visitas por categoría",
  vw_dominios_visitados:       "Dominios visitados",
  vw_dominios_interes_visitados:"Dominios interés",
  vw_acuerdosprivados:         "Acuerdos privados",
};

const USO_VW = new Set([
  "vw_visitas_zona", "vw_visitas_top10", "vw_visitas_interes_mineduc",
  "vw_control_parental", "vw_seguridad", "vw_visitas_por_categoria",
  "vw_dominios_visitados", "vw_dominios_interes_visitados",
]);

const FIN_MES_VW = new Set(["vw_maliciosas"]);

function normTipo(tipo) {
  return String(tipo ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function tipoToVw(tipo) {
  return TIPO_TO_VW[normTipo(tipo)] ?? null;
}

function isUsoTipo(tipo) {
  return USO_VW.has(tipoToVw(tipo));
}

function isFinMesTipo(tipo) {
  return FIN_MES_VW.has(tipoToVw(tipo));
}

function getDeadline(periodoStr, tipo) {
  if (!periodoStr) return null;
  const d = new Date(periodoStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m0 = d.getMonth();
  if (isFinMesTipo(tipo)) {
    return new Date(y, m0 + 2, 0);
  }
  const n = isUsoTipo(tipo) ? 10 : 5;
  return nthBusinessDay(y, m0, n);
}

function getDeadlineDescription(tipo) {
  if (isFinMesTipo(tipo)) return "Este reporte debe ser enviado antes del último día del mes siguiente";
  const n = isUsoTipo(tipo) ? 10 : 5;
  return `Este reporte debe ser enviado dentro de los primeros ${n} días hábiles del mes`;
}

function isPlazoValido(periodoStr, timestampStr, tipo) {
  if (!periodoStr || !timestampStr) return null;
  const received = new Date(timestampStr);
  const deadline = getDeadline(periodoStr, tipo);
  if (!deadline || isNaN(received.getTime())) return null;
  const recKey = received.getFullYear() * 10000 + (received.getMonth() + 1) * 100 + received.getDate();
  const dlKey = deadline.getFullYear() * 10000 + (deadline.getMonth() + 1) * 100 + deadline.getDate();
  return recKey <= dlKey;
}

function formatFecha(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} / ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatPeriodo(periodoStr) {
  if (!periodoStr) return "-";
  const d = new Date(periodoStr);
  if (isNaN(d.getTime())) return periodoStr;
  return `${MESES_ES[d.getMonth()]} ${d.getFullYear()}`;
}

function formatTipo(tipo) {
  if (!tipo) return "-";
  const vw = tipoToVw(tipo);
  return vw ? (VW_LABELS[vw] ?? tipo) : tipo;
}

function formatNombreArchivo(nombre) {
  if (!nombre) return "-";
  return nombre.replace(/\.[^.]+$/, "");
}

function formatErrors(errors) {
  if (!errors) return "Procesado sin errores";
  let arr = errors;
  if (typeof errors === "string") {
    try { arr = JSON.parse(errors); } catch { return errors; }
  }
  if (!Array.isArray(arr) || arr.length === 0) return "Procesado sin errores";
  return arr
    .map((e) => {
      if (typeof e !== "object") return String(e);
      const parts = [];
      if (e.description) parts.push(e.description);
      if (e.exception) parts.push(e.exception);
      return parts.length ? parts.join(": ") : JSON.stringify(e);
    })
    .join("\n");
}

function getColorConfig(valid) {
  if (valid === null) return { bg: "#F5F5F5", text: "#333" };
  return valid ? { bg: "#C8E6C9", text: "#1B5E20" } : { bg: "#FFCDD2", text: "#BE0A1F" };
}

const UUIDReporte = () => {
  const { pageTitle, setPageTitle } = useContext(GeneralContext);
  const [uuid, setUuid] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [operadores, setOperadores] = useState([]);

  useEffect(() => {
    setPageTitle("UUID Reporte");
  }, [setPageTitle]);

  useEffect(() => {
    axios.getAllOperadores()
      .then((res) => setOperadores(res.data || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(false);
    setLoading(true);
    setResponseData(null);
    setShowRaw(false);

    if (!uuid.trim()) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.getUUIDReporte(uuid.trim());
      setResponseData(Array.isArray(response.data) ? response.data[0] : response.data);
    } catch (err) {
      setError(true);
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  };

  const operadorName = useMemo(() => {
    if (!responseData?.metadata_operador) return null;
    const id = responseData.metadata_operador;
    const op = operadores.find(
      (o) => o.id_operador === id || o.id === id || String(o.id_operador) === String(id)
    );
    return op?.nombre_operador || op?.operador || op?.nombre || `Operador ${id}`;
  }, [responseData, operadores]);

  const plazoValido = responseData
    ? isPlazoValido(responseData.periodo, responseData.metadata_timestamp, responseData.metadata_tipo)
    : null;
  const integridadValida = responseData ? !responseData.error : null;
  const plazoConfig = getColorConfig(plazoValido);
  const integridadConfig = getColorConfig(integridadValida);

  return (
    <PageBody>
      <PageBgBlock pad="3rem 6rem">
        <Typography variant="h1" align="center" sx={{ marginBottom: "1.2rem" }}>
          ESTADO DEL ENVÍO DE REPORTES
        </Typography>

        <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
          <Box justifyContent="center" sx={{ maxWidth: "80%", margin: "0 auto", display: "flex" }}>
            <Typography variant="body" align="center" sx={{ marginBottom: "40px" }}>
              Introduzca el UUID tal como lo recibió desde el webservice. Aquí podrá obtener
              información detallada sobre su trazabilidad y los eventos que ocurrieron a lo largo
              de su proceso,{" "}
              <b>
                debe tener en cuenta que si los datos fueron enviados fuera de plazo y/o el proceso
                de datos fue inválido se harán descuentos en su prefactura.
              </b>
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <FormControl sx={{ m: 1, minWidth: "100%", maxWidth: "100%", marginBottom: "39px" }}>
              <TextField
                required
                id="outlined-required"
                label="Ingresa UUID"
                size="small"
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
              />
            </FormControl>
            <PageButtonBlack loading={loading} width="312px">
              Obtener información
            </PageButtonBlack>
          </form>

          {!loading && responseData && (
            <>
              {operadorName && (
                <Box
                  sx={{
                    bgcolor: "#BBDEFB",
                    py: 1,
                    px: 2,
                    textAlign: "center",
                    borderRadius: 1,
                    mt: 4,
                  }}
                >
                  <Typography variant="body" sx={{ fontWeight: 700 }}>
                    {operadorName}
                  </Typography>
                </Box>
              )}

              <Typography
                variant="h2"
                align="center"
                sx={{ mt: operadorName ? 2 : 4, fontWeight: 700 }}
              >
                Reporte: {formatTipo(responseData.metadata_tipo)}
              </Typography>

              <Typography variant="body" align="center" sx={{ display: "block", mb: 3 }}>
                <b>Nombre del archivo:</b> {formatNombreArchivo(responseData.metadata_nombre)}
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                <Grid size={6}>
                  <Box
                    sx={{
                      backgroundColor: plazoConfig.bg,
                      height: "100%",
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 150,
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                      <Typography variant="h5" sx={{ color: plazoConfig.text, fontWeight: 700 }}>
                        PLAZO DE DATOS:{" "}
                        {plazoValido === null ? "-" : plazoValido ? "Dentro" : "Fuera"}
                      </Typography>
                      <Typography variant="small">
                        ({getDeadlineDescription(responseData.metadata_tipo)})
                      </Typography>
                      <Typography variant="body" sx={{ fontWeight: 700, paddingTop: 1 }}>
                        PERIODO DEL REPORTE:
                      </Typography>
                      <Typography variant="body">
                        {formatPeriodo(responseData.periodo)}
                      </Typography>
                      <Typography variant="body" sx={{ fontWeight: 700, paddingTop: 1 }}>
                        INGRESO DE LOS DATOS A SAGEC:
                      </Typography>
                      <Typography variant="body">
                        {formatFecha(responseData.metadata_timestamp)}
                      </Typography>
                      <Typography variant="body" sx={{ fontWeight: 700, paddingTop: 1 }}>
                        FECHA Y HORA DE ORIGEN DE LOS DATOS:
                      </Typography>
                      <Typography variant="body">
                        {formatFecha(responseData.metadata_date)}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>

                <Grid size={6}>
                  <Box
                    sx={{
                      backgroundColor: integridadConfig.bg,
                      height: "100%",
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 150,
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={1} sx={{ textAlign: "center" }}>
                      <Typography variant="h5" sx={{ color: integridadConfig.text, fontWeight: 700 }}>
                        CHEQUEO DE INTEGRIDAD:{" "}
                        {integridadValida === null ? "-" : integridadValida ? "Válido" : "Inválido"}
                      </Typography>
                      <Typography variant="small">
                        Verifica que los datos cumplan con el tipo esperado y valores dentro de
                        rangos válidos, asegurando consistencia y cumplimiento de reglas de negocio.
                      </Typography>
                      <Typography variant="body" sx={{ fontWeight: 700, paddingTop: 2 }}>
                        MENSAJE:
                      </Typography>
                      <Typography
                        variant="small"
                        sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}
                      >
                        {formatErrors(responseData.errors)}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ textAlign: "center", marginTop: 4 }}>
                <PageButtonWhite onClick={() => setShowRaw(!showRaw)} width="312px">
                  {showRaw ? "Ocultar datos (raw)" : "Mostrar los datos (raw)"}
                </PageButtonWhite>
                <Box sx={{ width: "300px", paddingTop: 2, paddingBottom: 3, margin: "auto" }}>
                  <Typography variant="small">
                    Es posible mostrar los datos del informe en un formato raw para más detalles en
                    un formato más técnico.
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {showRaw && responseData && (
            <Box style={{ marginTop: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  padding: "10px 20px",
                  backgroundColor: "#E4E4E4",
                  borderRadius: "10px",
                  fontSize: "12px",
                }}
              >
                <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                  {JSON.stringify(responseData, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}

          {error && !responseData && (
            <Box style={{ marginTop: "39px" }}>
              <Alert variant="outlined" severity="error">
                <Box justifyContent="center" sx={{ maxWidth: "100%", margin: "0 auto", display: "flex" }}>
                  <Typography variant="body" align="left">
                    UUID no encontrado.
                  </Typography>
                </Box>
              </Alert>
            </Box>
          )}
        </Box>
      </PageBgBlock>
    </PageBody>
  );
};

export default UUIDReporte;
